import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { CourseListApiItem } from "@features/CourseBuilder/components/CourseTab/types";
import type { CourseCategoryGroupItem } from "@features/CourseBuilder/components/CourseTab/types";
import { mapListCourseApiToBuilderState } from "@features/CourseBuilder/components/CourseTab/utils/mapListCourseToCourseBuilderState";
import { mapCategoryToCourseBuilderState } from "@features/CourseBuilder/components/CourseTab/utils/mapCategoryToCourseBuilderState";
import {
	createDefaultAttachmentItem,
	type CourseLabeledAttachmentItem,
} from "@features/CourseBuilder/components/CourseTab/utils/courseAttachmentUtils";

export interface LabEquipmentDetails {
	id: string;
	labName: string;
	numericId: number;
	equipments: Array<{
		equipmentId: string;
		equipmentLabel: string;
		requiredCount: number;
		availableCount: number;
	}>;
}

export interface CourseBuilderFormState {
	// Step 1: Course Details
	courseDetails: {
		courseName: string;
		courseType: string;
		courseLevel: string;
		duration: string;
		durationUnit: string;
		totalStudentsCount: number;
		sector: string;
		/** Display label for edit hydration when the selected sector is not yet in paginated options */
		sectorName: string;
		teachingMode: string;
		courseNotifyingAuthorityId: string;
		description: string;
		overview: string;
		lastReview: string;
		eligibilityCriteria: string;
		courseMode: string;
		courseCategoryId: string;
		/** Display label for edit hydration when the selected category is not yet in paginated options */
		courseCategoryName: string;
		/** Selected course content subjects (UUIDs). */
		subjectIds: string[];
		/** Hydration labels for subjects not yet loaded in paginated options. */
		subjectItems: Array<{ uuid: string; name: string; description?: string | null }>;
		studentMinimumQualification: string;
		matricStream: string;
		/** Conventional teaching mode only */
		totalMarks: string;
		practicalMarksPercent: string;
		theoreticalMarksPercent: string;
		/** CBTA teaching mode only */
		totalModules: string;
		examSystemId: string;
		/** Required when exam system is Year or Semester for courses longer than 1 year */
		/** Read-only display; auto-calculated from exam system + duration */
		promotionStages: string;
		/** Read-only display; auto-calculated from exam system + duration */
		termCount: string;
		/** PVTC notifying authority only */
		hasOjt: boolean;
		/** OJT duration in months (required when hasOjt is true) */
		ojtDuration: string;
		/** Course Content — shared */
		courseCode: string;
		trainingHours: string;
		notificationDate: string;
		version: string;
		/** Conventional teaching mode — course equivalence (Matric, Intermediate, …) */
		courseEquivalence: string;
		/** Course Content — competency point catalog UUIDs */
		competencyPointIds: string[];
		/** Hydration labels for competency points not yet loaded in options. */
		competencyPointItems: Array<{ uuid: string; name: string }>;
		/** Course curriculum — age eligibility */
		minAge: string;
		maxAge: string;
		relaxationPeriod: string;
		/** Assessment bodies offered for this course (UUIDs). */
		assessmentBodyIds: string[];
		/** Hydration labels for bodies not yet loaded in options. */
		assessmentBodyItems: Array<{ uuid: string; name: string }>;
	};

	// Step 2: Laboratories
	laboratories: {
		laboratories: LabEquipmentDetails[];
	};

	// Step 3: Faculty Details
	facultyDetails: {
		facultyQualificationId: string[];
		/** Hydration labels for qualifications not yet loaded in paginated options. */
		facultyQualificationItems: Array<{ uuid: string; name: string }>;
		facultyBriefRequirements: string;
		facultyExperienceArea: string;
		facultyExperienceYears: number;
		totalFacultyMemberCount: number;
	};

	// Step 4: Attachments
	attachments: {
		items: CourseLabeledAttachmentItem[];
	};

	// UI State
	currentStep: number;
	isFormValid: boolean;
}

const initialState: CourseBuilderFormState = {
	courseDetails: {
		courseName: "",
		courseType: "",
		courseLevel: "",
		duration: "",
		durationUnit: "",
		totalStudentsCount: 0,
		sector: "",
		sectorName: "",
		teachingMode: "",
		courseNotifyingAuthorityId: "",
		description: "",
		overview: "",
		lastReview: "",
		eligibilityCriteria: "",
		courseMode: "true",
		courseCategoryId: "",
		courseCategoryName: "",
		subjectIds: [],
		subjectItems: [],
		studentMinimumQualification: "",
		matricStream: "",
		totalMarks: "",
		practicalMarksPercent: "",
		theoreticalMarksPercent: "",
		totalModules: "",
		examSystemId: "",
		promotionStages: "",
		termCount: "",
		hasOjt: false,
		ojtDuration: "",
		courseCode: "",
		trainingHours: "",
		notificationDate: "",
		version: "",
		courseEquivalence: "",
		competencyPointIds: [],
		competencyPointItems: [],
		minAge: "",
		maxAge: "",
		relaxationPeriod: "",
		assessmentBodyIds: [],
		assessmentBodyItems: [],
	},
	laboratories: {
		laboratories: [],
	},
	facultyDetails: {
		facultyQualificationId: [],
		facultyQualificationItems: [],
		facultyBriefRequirements: "",
		facultyExperienceArea: "",
		facultyExperienceYears: 0,
		totalFacultyMemberCount: 0,
	},
	attachments: {
		items: [createDefaultAttachmentItem()],
	},
	currentStep: 0,
	isFormValid: false,
};

const courseBuilderSlice = createSlice({
	name: "courseBuilder",
	initialState,
	reducers: {
		// Update specific step data
		updateCourseDetails: (state, action: PayloadAction<Partial<CourseBuilderFormState["courseDetails"]>>) => {
			state.courseDetails = { ...state.courseDetails, ...action.payload };
		},
		updateLaboratories: (state, action: PayloadAction<Partial<CourseBuilderFormState["laboratories"]>>) => {
			state.laboratories = { ...state.laboratories, ...action.payload };
		},
		updateFacultyDetails: (state, action: PayloadAction<Partial<CourseBuilderFormState["facultyDetails"]>>) => {
			state.facultyDetails = { ...state.facultyDetails, ...action.payload };
		},
		updateAttachments: (state, action: PayloadAction<Partial<CourseBuilderFormState["attachments"]>>) => {
			state.attachments = { ...state.attachments, ...action.payload };
		},

		// Navigation
		setCurrentStep: (state, action: PayloadAction<number>) => {
			state.currentStep = action.payload;
		},
		nextStep: state => {
			if (state.currentStep < 5) {
				state.currentStep += 1;
			}
		},
		previousStep: state => {
			if (state.currentStep > 0) {
				state.currentStep -= 1;
			}
		},

		// Form validation
		setFormValid: (state, action: PayloadAction<boolean>) => {
			state.isFormValid = action.payload;
		},

		// Reset form (when modal closes or form is submitted)
		resetForm: () => {
			return initialState;
		},

		/** Populate full builder state from a list-course API row (edit / revise flow). */
		loadCourseForEdit: (_state, action: PayloadAction<CourseListApiItem>) => {
			return mapListCourseApiToBuilderState(action.payload);
		},

		/** Prefill step-1 basics from a category row (Add New Content flow). */
		loadCategoryForNewContent: (_state, action: PayloadAction<CourseCategoryGroupItem>) => {
			return mapCategoryToCourseBuilderState(action.payload);
		},

		// Reset specific step
		resetStep: (state, action: PayloadAction<keyof Omit<CourseBuilderFormState, "currentStep" | "isFormValid">>) => {
			const stepKey = action.payload;
			if (stepKey === "courseDetails") {
				state.courseDetails = initialState.courseDetails;
			} else if (stepKey === "laboratories") {
				state.laboratories = initialState.laboratories;
			} else if (stepKey === "facultyDetails") {
				state.facultyDetails = initialState.facultyDetails;
			} else if (stepKey === "attachments") {
				state.attachments = initialState.attachments;
			}
		},
	},
});

export const {
	updateCourseDetails,
	updateLaboratories,
	updateFacultyDetails,
	updateAttachments,
	setCurrentStep,
	nextStep,
	previousStep,
	setFormValid,
	resetForm,
	resetStep,
	loadCourseForEdit,
	loadCategoryForNewContent,
} = courseBuilderSlice.actions;

export default courseBuilderSlice.reducer;
